import { useState, useEffect } from 'react';
import { ForumRoom } from '../types';

interface RoomModalProps {
  room: ForumRoom | null;
  onSave: (data: Omit<ForumRoom, 'id' | 'topicCount' | 'postCount' | 'createdAt'>) => Promise<void>;
  onClose: () => void;
}

export default function RoomModal({ room, onSave, onClose }: RoomModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [order, setOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (room) {
      setName(room.name);
      setDescription(room.description);
      setImageUrl(room.imageUrl || '');
      setOrder(room.order);
      setIsActive(room.isActive);
    }
  }, [room]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await onSave({
        name,
        description,
        imageUrl: imageUrl || undefined,
        order,
        isActive,
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save room');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{room ? 'Edit Room' : 'Add Room'}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Image URL (optional)</label>
                <input
                  type="url"
                  className="form-control"
                  value={imageUrl}
                  onChange={e => setImageUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Order</label>
                <input
                  type="number"
                  className="form-control"
                  value={order}
                  onChange={e => setOrder(parseInt(e.target.value))}
                  required
                  min="0"
                />
                <small className="form-hint">Lower numbers appear first</small>
              </div>

              <div className="mb-3">
                <label className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                  />
                  <span className="form-check-label">Active</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
